import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Users, Search, ChevronRight, Shield, User, Mail, Phone,
  Calendar, X, UserCheck, UserX, ChevronDown, Trash2, AlertTriangle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'parent', label: 'Parents' },
  { value: 'coach', label: 'Coaches' },
  { value: 'team_staff', label: 'Team Staff' },
  { value: 'team_mom', label: 'Team Moms' },
  { value: 'league_admin', label: 'League Admin' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'super_admin', label: 'Super Admin' }
];

const getRoleBadge = (role) => {
  const styles = {
    parent: 'bg-light-blue/10 text-light-blue',
    coach: 'bg-green-100 text-green-800',
    team_staff: 'bg-purple-100 text-purple-800',
    team_mom: 'bg-pink-100 text-pink-800',
    league_admin: 'bg-gold/20 text-yellow-800',
    treasurer: 'bg-emerald-100 text-emerald-800',
    super_admin: 'bg-red/10 text-red'
  };
  return styles[role] || 'bg-gray-100 text-gray-700';
};

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Phase: permanent user delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionEligibility, setDeletionEligibility] = useState(null);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [emailConfirmation, setEmailConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'league_admin';

  // Prefill search from ?search= URL param (used by deep-links from Player Quick Review)
  // and trigger an immediate fetch so the list is filtered without requiring user to press Enter.
  useEffect(() => {
    const initial = searchParams.get('search');
    if (initial) {
      setSearchTerm(initial);
      (async () => {
        setLoading(true);
        try {
          const params = new URLSearchParams();
          if (roleFilter !== 'all') params.append('role', roleFilter);
          params.append('search', initial);
          const res = await axios.get(`${API_URL}/api/admin/users?${params}`, { withCredentials: true });
          setUsers(res.data.users || []);
        } catch (error) {
          console.error('Failed to fetch users:', error);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [searchParams]);

  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (searchTerm) params.append('search', searchTerm);
      const res = await axios.get(`${API_URL}/api/admin/users?${params}`, { withCredentials: true });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const openUserDetail = async (u) => {
    setSelectedUser(u);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/${u.user_id}`, { withCredentials: true });
      setUserDetail(res.data);
    } catch (error) {
      console.error('Failed to fetch user detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeactivate = async (userId, isActive) => {
    const action = isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/${action}`, {}, { withCredentials: true });
      fetchUsers();
      if (userDetail?.user?.user_id === userId) {
        setUserDetail(prev => ({ ...prev, user: { ...prev.user, is_active: !isActive } }));
      }
    } catch (error) {
      alert(`Failed to ${action} user`);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(`Change this user's role to ${newRole}?`)) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      fetchUsers();
      if (userDetail?.user?.user_id === userId) {
        setUserDetail(prev => ({ ...prev, user: { ...prev.user, role: newRole } }));
      }
    } catch (error) {
      alert('Failed to update role');
    }
  };

  // Phase: open delete modal — fetch eligibility first
  const openDeleteModal = async (userId) => {
    setEmailConfirmation('');
    setDeleteError(null);
    setDeletionEligibility(null);
    setShowDeleteModal(true);
    setDeletionLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/users/${userId}/deletion-eligibility`,
        { withCredentials: true }
      );
      setDeletionEligibility(res.data);
    } catch (error) {
      setDeleteError(error.response?.data?.detail || 'Failed to load deletion eligibility');
    } finally {
      setDeletionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!userDetail?.user) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${userDetail.user.user_id}`,
        { withCredentials: true }
      );
      // Success: close modals, refresh list
      setShowDeleteModal(false);
      setSelectedUser(null);
      setUserDetail(null);
      setEmailConfirmation('');
      setDeletionEligibility(null);
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.blockers)) {
        setDeleteError(detail.blockers.join(' • '));
      } else {
        setDeleteError(typeof detail === 'string' ? detail : 'Failed to delete user');
      }
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    try { return format(parseISO(typeof d === 'string' ? d : new Date(d).toISOString()), 'MMM d, yyyy'); } catch { return 'N/A'; }
  };

  return (
    <ParentLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="admin-users-page">
        {/* Header */}
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Administration</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-navy uppercase tracking-tighter">Manage Users</h1>
          <p className="text-gray-500 text-sm mt-1">View and manage all registered parents, coaches, and staff</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onBlur={fetchUsers}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
              data-testid="user-search-input"
            />
          </form>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent bg-white"
            data-testid="user-role-filter"
          >
            {ROLE_OPTIONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Users', count: users.length, color: 'text-navy' },
            { label: 'Parents', count: users.filter(u => u.role === 'parent').length, color: 'text-light-blue' },
            { label: 'Coaches', count: users.filter(u => u.role === 'coach' || u.role === 'team_staff' || u.role === 'team_mom').length, color: 'text-green-600' },
            { label: 'Admins', count: users.filter(u => u.role === 'league_admin' || u.role === 'super_admin').length, color: 'text-red' }
          ].map(stat => (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className={`font-display text-2xl font-black ${stat.color}`}>{stat.count}</p>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* User List */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No users found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {users.map(u => (
              <div
                key={u.user_id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-navy/30 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => openUserDetail(u)}
                data-testid={`user-row-${u.user_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-navy" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-navy truncate">{u.name}</p>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <Badge className={`${getRoleBadge(u.role)} text-xs hidden sm:inline-flex`}>
                      {u.role?.replace('_', ' ')}
                    </Badge>
                    {u.player_count > 0 && (
                      <span className="text-xs text-gray-400">{u.player_count} child{u.player_count > 1 ? 'ren' : ''}</span>
                    )}
                    {u.is_active === false && (
                      <Badge className="bg-red/10 text-red text-xs">Inactive</Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* User Detail Drawer */}
        {selectedUser && (
          <>
            <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setSelectedUser(null)} />
            <div className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white z-50 shadow-2xl overflow-y-auto" data-testid="user-detail-drawer">
              {/* Header */}
              <div className="sticky top-0 bg-navy text-white p-4 flex items-center justify-between z-10">
                <h2 className="font-bold text-lg">User Details</h2>
                <button onClick={() => setSelectedUser(null)} className="text-white/70 hover:text-white" data-testid="close-user-detail">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {detailLoading ? (
                <div className="p-6 animate-pulse space-y-4">
                  <div className="h-16 bg-gray-200 rounded-lg" />
                  <div className="h-32 bg-gray-200 rounded-lg" />
                </div>
              ) : userDetail ? (
                <div className="p-5 space-y-5">
                  {/* User info */}
                  <div className="text-center pb-4 border-b border-gray-200">
                    <div className="w-16 h-16 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      {userDetail.user.picture ? (
                        <img src={userDetail.user.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 text-navy" />
                      )}
                    </div>
                    <h3 className="font-bold text-navy text-xl">{userDetail.user.name}</h3>
                    <Badge className={`${getRoleBadge(userDetail.user.role)} mt-1`}>
                      {userDetail.user.role?.replace('_', ' ')}
                    </Badge>
                    {userDetail.user.is_active === false && (
                      <Badge className="bg-red/10 text-red ml-2">Inactive</Badge>
                    )}
                  </div>

                  {/* Contact info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-700 break-all">{userDetail.user.email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{userDetail.user.phone || 'No phone'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Joined {formatDate(userDetail.user.created_at)}</span>
                    </div>
                  </div>

                  {/* Household */}
                  {userDetail.household && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Household</p>
                      <p className="text-sm text-gray-700">{userDetail.household.primary_contact_name || 'N/A'}</p>
                      <p className="text-xs text-gray-500">{
                        typeof userDetail.household.address === 'object' && userDetail.household.address
                          ? [userDetail.household.address.street, userDetail.household.address.city, userDetail.household.address.state, userDetail.household.address.zip].filter(Boolean).join(', ')
                          : (userDetail.household.address || 'No address')
                      }</p>
                      <p className="text-xs text-gray-500 mt-1">Balance: ${(userDetail.household.balance || 0).toFixed(2)}</p>
                    </div>
                  )}

                  {/* Players */}
                  {userDetail.players?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Children ({userDetail.players.length})</p>
                      <div className="space-y-2">
                        {userDetail.players.map(p => (
                          <div key={p.player_id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-navy text-sm">{p.first_name} {p.last_name}</p>
                              <p className="text-xs text-gray-500">
                                {p.age_group || 'No division'} {p.grade ? `• Grade ${p.grade}` : ''}
                              </p>
                            </div>
                            {p.registration ? (
                              <Badge className={p.registration.status === 'approved' ? 'bg-green-100 text-green-800 text-xs' : 'bg-yellow-100 text-yellow-800 text-xs'}>
                                {p.registration.status}
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-600 text-xs">No reg</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="border-t border-gray-200 pt-4 space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Actions</p>

                    {/* Role change (admins) */}
                    {(user?.role === 'super_admin' || user?.role === 'league_admin') && userDetail.user.user_id !== user.user_id && (
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Change Role</label>
                        <select
                          value={userDetail.user.role}
                          onChange={(e) => handleRoleChange(userDetail.user.user_id, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          data-testid="change-role-select"
                        >
                          {ROLE_OPTIONS.filter(r => {
                            if (r.value === 'all') return false;
                            // Only super_admin can assign protected roles (super_admin, treasurer, league_admin)
                            if ((r.value === 'league_admin' || r.value === 'super_admin' || r.value === 'treasurer') && user?.role !== 'super_admin') return false;
                            return true;
                          }).map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Activate/Deactivate */}
                    {userDetail.user.user_id !== user.user_id && (
                      <Button
                        variant="outline"
                        className={`w-full ${userDetail.user.is_active === false ? 'text-green-600 border-green-300 hover:bg-green-50' : 'text-red border-red/30 hover:bg-red/5'}`}
                        onClick={() => handleDeactivate(userDetail.user.user_id, userDetail.user.is_active !== false)}
                        data-testid="toggle-active-btn"
                      >
                        {userDetail.user.is_active === false ? (
                          <><UserCheck className="w-4 h-4 mr-2" /> Reactivate Account</>
                        ) : (
                          <><UserX className="w-4 h-4 mr-2" /> Deactivate Account</>
                        )}
                      </Button>
                    )}

                    {/* Permanent delete (admin-only, never self) */}
                    {isAdmin && userDetail.user.user_id !== user.user_id && (
                      <Button
                        variant="outline"
                        className="w-full text-red border-red/40 hover:bg-red/10"
                        onClick={() => openDeleteModal(userDetail.user.user_id)}
                        data-testid="delete-user-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete User Permanently
                      </Button>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </>
        )}

        {/* Permanent Delete Confirmation Modal */}
        {showDeleteModal && userDetail?.user && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[60]"
              onClick={() => !deleting && setShowDeleteModal(false)}
            />
            <div
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-md bg-white rounded-xl shadow-2xl z-[70] overflow-hidden"
              data-testid="delete-user-modal"
            >
              {/* Header */}
              <div className="bg-red text-white px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  <h3 className="font-bold text-base">Permanently Delete User</h3>
                </div>
                <button
                  onClick={() => !deleting && setShowDeleteModal(false)}
                  className="text-white/80 hover:text-white disabled:opacity-50"
                  disabled={deleting}
                  data-testid="close-delete-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <p className="text-sm text-gray-700">
                    You are about to permanently delete:
                  </p>
                  <p className="font-semibold text-navy mt-1" data-testid="delete-user-name">
                    {userDetail.user.name}
                  </p>
                  <p className="text-xs text-gray-500 break-all">{userDetail.user.email}</p>
                </div>

                {deletionLoading ? (
                  <div className="py-6 text-center text-sm text-gray-500">
                    Checking eligibility…
                  </div>
                ) : deletionEligibility ? (
                  <>
                    {!deletionEligibility.can_delete ? (
                      <div
                        className="bg-red/10 border border-red/30 rounded-lg p-3"
                        data-testid="delete-blockers"
                      >
                        <p className="text-xs font-bold uppercase tracking-wider text-red mb-2">
                          Deletion Blocked
                        </p>
                        <ul className="text-sm text-red/90 space-y-1 list-disc list-inside">
                          {deletionEligibility.blockers.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                        <p className="text-xs text-gray-600 mt-3">
                          Resolve these issues (e.g. archive the team, transfer the
                          household primary, demote the last super admin) before
                          attempting deletion.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div
                          className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"
                          data-testid="delete-cleanup-summary"
                        >
                          <p className="text-xs font-bold uppercase tracking-wider text-yellow-800 mb-2">
                            Cleanup Summary
                          </p>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• Teams to unlink: <span className="font-semibold">{deletionEligibility.cleanup_summary.teams_to_unlink}</span></li>
                            <li>• Parent/guardian rows to remove: <span className="font-semibold">{deletionEligibility.cleanup_summary.parent_guardian_rows_to_remove}</span></li>
                            <li>• Sessions to revoke: <span className="font-semibold">{deletionEligibility.cleanup_summary.sessions_to_revoke}</span></li>
                            <li>• Push subscriptions to remove: <span className="font-semibold">{deletionEligibility.cleanup_summary.push_subscriptions_to_remove}</span></li>
                            <li>• Household to remove: <span className="font-semibold">{deletionEligibility.cleanup_summary.household_to_remove ? 'Yes' : 'No'}</span></li>
                          </ul>
                          <p className="text-xs text-gray-600 mt-2">
                            This action is <span className="font-semibold">permanent</span> and cannot be undone.
                          </p>
                        </div>

                        <div>
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1">
                            Type the user's email to confirm
                          </label>
                          <p className="text-xs text-gray-500 mb-2 break-all">
                            Required: <span className="font-mono">{userDetail.user.email}</span>
                          </p>
                          <input
                            type="text"
                            value={emailConfirmation}
                            onChange={(e) => setEmailConfirmation(e.target.value)}
                            placeholder={userDetail.user.email}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-red focus:border-transparent"
                            data-testid="delete-email-confirm-input"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            disabled={deleting}
                          />
                        </div>
                      </>
                    )}
                  </>
                ) : null}

                {deleteError && (
                  <div
                    className="bg-red/10 border border-red/30 rounded-lg p-3 text-sm text-red"
                    data-testid="delete-error"
                  >
                    {deleteError}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2 bg-gray-50">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  data-testid="cancel-delete-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  disabled={
                    deleting ||
                    deletionLoading ||
                    !deletionEligibility?.can_delete ||
                    emailConfirmation.trim().toLowerCase() !==
                      (userDetail.user.email || '').trim().toLowerCase()
                  }
                  className="bg-red hover:bg-red/90 text-white disabled:opacity-50"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Deleting…' : 'Delete Permanently'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </ParentLayout>
  );
}
